import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import {
  Award, FileCheck, Search, Plus, ArrowLeft, Download, Eye, EyeOff, Ban, RotateCcw,
  ShieldCheck, Clock, Copy, ExternalLink, Palette, ClipboardList, RefreshCw,
  CheckCircle2, Loader2,
} from "lucide-react";
import { DashboardLayout } from "@/components/DashboardLayout";
import { StatCard } from "@/components/StatCard";
import { StatusBadge } from "@/components/StatusBadge";
import { LoadingBlock, EmptyState } from "@/components/Pagination";
import { api, formatApiError } from "@/lib/api";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

// =========================================================================
// ADMIN CERT DASHBOARD
// =========================================================================
export function AdminCertDashboardPage() {
  const [totals, setTotals] = useState(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/admin/certificate-dashboard");
        setTotals(data.data.totals);
      } catch (err) { toast.error(formatApiError(err)); }
      finally { setLoading(false); }
    })();
  }, []);
  if (loading) return <DashboardLayout title="Certificates"><LoadingBlock /></DashboardLayout>;
  if (!totals) return null;
  return (
    <DashboardLayout title="Certificate Management" breadcrumbs={["Super Admin", "Certificates"]}>
      <div className="space-y-6" data-testid="admin-cert-dashboard">
        <section className="grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
          <StatCard label="Total issued" value={totals.total} icon="Award" tone="navy" testId="cert-kpi-total" index={0} />
          <StatCard label="Valid" value={totals.valid} icon="ShieldCheck" tone="green" testId="cert-kpi-valid" index={1} />
          <StatCard label="Expired" value={totals.expired} icon="Clock" tone="amber" testId="cert-kpi-expired" index={2} />
          <StatCard label="Revoked" value={totals.revoked} icon="Ban" tone="rose" testId="cert-kpi-revoked" index={3} />
        </section>
        <section className="grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
          <StatCard label="Reissued" value={totals.reissued} icon="RotateCcw" tone="slate" testId="cert-kpi-reissued" index={4} />
          <StatCard label="Pending approval" value={totals.pending_approval} icon="ClipboardCheck" tone="amber" testId="cert-kpi-pending" index={5} />
          <StatCard label="Renewal due" value={totals.renewal_due} icon="RefreshCw" tone="navy" testId="cert-kpi-renewal" index={6} />
          <StatCard label="This month" value={totals.this_month} icon="TrendingUp" tone="green" testId="cert-kpi-month" index={7} />
        </section>
        <div className="grid gap-4 sm:grid-cols-4">
          <QuickLink to="/app/super-admin/certificate-templates" icon={Palette} title="Templates" desc="Design + preview" />
          <QuickLink to="/app/super-admin/certificates" icon={Award} title="Register" desc="All certificates" />
          <QuickLink to="/app/super-admin/certificates/issue" icon={Plus} title="Manual issue" desc="Custom certificate" />
          <QuickLink to="/app/super-admin/certificate-renewals" icon={RefreshCw} title="Renewals" desc="Expiring soon" />
        </div>
      </div>
    </DashboardLayout>
  );
}

function QuickLink({ to, icon: Icon, title, desc }) {
  return (
    <Link to={to} className="rounded-2xl border border-slate-200 bg-white p-5 hover:border-[#101876]/40 hover:shadow-md transition" data-testid={`quicklink-${title.toLowerCase().replace(/\s+/g,"-")}`}>
      <Icon className="h-5 w-5 text-[#101876]" />
      <div className="mt-3 font-bold text-sm">{title}</div>
      <div className="text-xs text-slate-500">{desc}</div>
    </Link>
  );
}

// =========================================================================
// TEMPLATES LIST
// =========================================================================
export function CertTemplateListPage() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/certificate-templates");
      setRows(data.data.templates);
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const preview = (tid) => {
    window.open(`${BACKEND}/api/admin/certificate-templates/${tid}/preview`, "_blank");
  };

  const setStatus = async (tid, status) => {
    // Draft → active moves the template into the issuable set (the
    // certificate-issue UI filters on `status === "active"`). Active → draft
    // hides it from issuance without deleting the config.
    try {
      await api.patch(`/admin/certificate-templates/${tid}/status`, { status });
      toast.success(status === "active" ? "Template published." : "Template moved back to draft.");
      load();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  return (
    <DashboardLayout title="Certificate Templates" breadcrumbs={["Super Admin", "Templates"]}>
      <div className="space-y-4" data-testid="template-list">
        <div className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-xs text-amber-900" data-testid="template-design-notice">
          <span className="font-bold">Unified design:</span> every template on the platform uses the
          <span className="font-bold"> SIHMT Certificate of Excellence</span> layout (shield, gold cursive
          name, ribbon, footer strip). You can create as many templates as you like — the
          <span className="italic"> matter</span> (header, signatory, academy, footer, colors,
          certificate type) is per-template, but the visual design stays the same across the platform.
        </div>
        <div className="flex items-center gap-3">
          <Link to="/app/super-admin/certificate-templates/new"
            className="rounded-md bg-[#101876] px-4 py-2 text-xs font-bold text-white hover:bg-[#0c135c] flex items-center gap-1" data-testid="btn-new-template">
            <Plus className="h-3.5 w-3.5" /> New template
          </Link>
        </div>
        {loading ? <LoadingBlock /> : rows.length === 0 ? (
          <EmptyState title="No templates yet" description="Create a certificate template to start issuing certificates." icon={Palette} />
        ) : (
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {rows.map((t) => (
              <div key={t.id} className="rounded-2xl border border-slate-200 bg-white overflow-hidden" data-testid={`template-${t.id}`}>
                <div className="h-28 bg-gradient-to-br p-4 text-white" style={{
                  background: `linear-gradient(135deg, ${t.primary_color || "#101876"}, ${t.secondary_color || "#329112"})`,
                }}>
                  <div className="text-xs opacity-80 font-bold uppercase tracking-widest">{t.certificate_type?.replace(/_/g, " ")}</div>
                  <div className="text-sm font-bold mt-1 line-clamp-2">{t.template_name}</div>
                </div>
                <div className="p-4 space-y-2">
                  <div className="flex items-center gap-2 text-xs">
                    <span className="font-mono text-slate-500">{t.template_code}</span>
                    <StatusBadge status={t.status} />
                    {t.is_default && <span className="rounded bg-amber-100 px-2 py-0.5 text-[10px] font-bold text-amber-800 uppercase tracking-wide">Default</span>}
                  </div>
                  <div className="text-xs text-slate-500 line-clamp-2">{t.header_text}</div>
                  <div className="flex flex-wrap gap-2 pt-2">
                    <button onClick={() => preview(t.id)} className="flex-1 rounded-md border border-slate-200 px-3 py-1.5 text-xs font-bold text-slate-700 hover:bg-slate-50 flex items-center justify-center gap-1" data-testid={`template-preview-${t.id}`}>
                      <Eye className="h-3.5 w-3.5" /> Preview
                    </button>
                    <Link to={`/app/super-admin/certificate-templates/${t.id}/edit`} className="flex-1 rounded-md bg-[#101876] px-3 py-1.5 text-xs font-bold text-white hover:bg-[#0c135c] text-center" data-testid={`template-edit-${t.id}`}>Edit</Link>
                  </div>
                  {t.status === "draft" && (
                    <button onClick={() => setStatus(t.id, "active")} className="w-full rounded-md bg-[#329112] px-3 py-1.5 text-xs font-bold text-white hover:bg-[#26720d] flex items-center justify-center gap-1" data-testid={`template-publish-${t.id}`}>
                      <CheckCircle2 className="h-3.5 w-3.5" /> Publish template
                    </button>
                  )}
                  {t.status === "active" && !t.is_default && (
                    <button onClick={() => setStatus(t.id, "draft")} className="w-full rounded-md border border-amber-300 bg-amber-50 px-3 py-1.5 text-xs font-bold text-amber-800 hover:bg-amber-100 flex items-center justify-center gap-1" data-testid={`template-unpublish-${t.id}`}>
                      <EyeOff className="h-3.5 w-3.5" /> Unpublish (move to draft)
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}

// =========================================================================
// TEMPLATE FORM (create/edit)
// =========================================================================
export function CertTemplateFormPage({ mode = "create" }) {
  const { tid } = useParams();
  const nav = useNavigate();
  const [form, setForm] = useState({
    template_name: "", template_code: "",
    certificate_type: "certification_program", orientation: "landscape",
    header_text: "Certificate of Completion",
    footer_text: "This certificate has been issued by SIHMT and can be verified online.",
    signatory_name: "Dr. Steward Learning Cloud",
    signatory_designation: "Director – Steward Institute of Healthcare Management & Technology",
    academy_name: "Steward Institute of Healthcare Management & Technology",
    company_name: "Steward Healthcare India Pvt. Ltd.",
    primary_color: "#101876", secondary_color: "#329112",
    status: "draft",
  });
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    if (mode === "edit" && tid) {
      (async () => {
        try {
          const { data } = await api.get(`/admin/certificate-templates/${tid}`);
          setForm((p) => ({ ...p, ...data.data.template }));
        } catch (err) { toast.error(formatApiError(err)); }
      })();
    }
  }, [mode, tid]);

  const submit = async () => {
    setSaving(true);
    try {
      if (mode === "edit") {
        await api.put(`/admin/certificate-templates/${tid}`, form);
      } else {
        await api.post("/admin/certificate-templates", form);
      }
      toast.success("Saved.");
      nav("/app/super-admin/certificate-templates");
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setSaving(false); }
  };

  const field = (k, label, extra = {}) => (
    <label className="block text-sm">
      <div className="mb-1 text-xs font-bold uppercase tracking-widest text-slate-500">{label}</div>
      <input value={form[k] ?? ""} onChange={(e) => setForm((p) => ({ ...p, [k]: e.target.value }))}
        className="w-full rounded-md border border-slate-200 px-3 py-2 text-sm" data-testid={`tf-${k}`} {...extra} />
    </label>
  );

  return (
    <DashboardLayout title={mode === "edit" ? "Edit template" : "New template"} breadcrumbs={["Super Admin", "Templates", mode]}>
      <div className="max-w-4xl space-y-4" data-testid="template-form">
        <div className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-xs text-amber-900" data-testid="template-form-notice">
          <span className="font-bold">Note:</span> the platform&apos;s certificate design (SIHMT Excellence layout — shield,
          gold cursive name, ribbon, footer strip) is fixed. This form controls the
          <span className="font-bold"> matter</span> for this template: header/footer text, signatory, academy,
          certificate type, and accent colors. Learner name, course title, issue date and QR are filled in
          automatically at issue time.
        </div>
        <div className="rounded-2xl border border-slate-200 bg-white p-6 space-y-4">
          <div className="grid gap-3 sm:grid-cols-2">
            {field("template_name", "Template name *")}
            {field("template_code", "Template code *", { disabled: mode === "edit" })}
            <label className="block text-sm">
              <div className="mb-1 text-xs font-bold uppercase tracking-widest text-slate-500">Type</div>
              <select value={form.certificate_type} onChange={(e) => setForm((p) => ({ ...p, certificate_type: e.target.value }))} className="w-full rounded-md border border-slate-200 px-3 py-2 text-sm">
                <option value="course_completion">Course Completion</option>
                <option value="exam_passing">Exam Passing</option>
                <option value="certification_program">Certification Program</option>
                <option value="compliance_training">Compliance Training</option>
                <option value="participation">Participation</option>
                <option value="attendance">Attendance</option>
              </select>
            </label>
            <label className="block text-sm">
              <div className="mb-1 text-xs font-bold uppercase tracking-widest text-slate-500">Orientation</div>
              <select value={form.orientation} onChange={(e) => setForm((p) => ({ ...p, orientation: e.target.value }))} className="w-full rounded-md border border-slate-200 px-3 py-2 text-sm">
                <option value="landscape">Landscape</option>
                <option value="portrait">Portrait</option>
              </select>
            </label>
          </div>
        </div>
        <div className="rounded-2xl border border-slate-200 bg-white p-6 space-y-4">
          <div className="text-xs font-bold uppercase tracking-widest text-slate-500">Text content</div>
          {field("header_text", "Header text")}
          <label className="block text-sm">
            <div className="mb-1 text-xs font-bold uppercase tracking-widest text-slate-500">Footer text</div>
            <textarea rows={2} value={form.footer_text || ""} onChange={(e) => setForm((p) => ({ ...p, footer_text: e.target.value }))}
              className="w-full rounded-md border border-slate-200 px-3 py-2 text-sm" />
          </label>
          {field("signatory_name", "Signatory name")}
          {field("signatory_designation", "Signatory designation")}
          {field("academy_name", "Academy name")}
          {field("company_name", "Company name")}
        </div>
        <div className="rounded-2xl border border-slate-200 bg-white p-6 space-y-4">
          <div className="text-xs font-bold uppercase tracking-widest text-slate-500">Colors</div>
          <div className="grid gap-3 sm:grid-cols-2">
            <label className="block text-sm">
              <div className="mb-1 text-xs font-bold uppercase tracking-widest text-slate-500">Primary color</div>
              <div className="flex items-center gap-2">
                <input type="color" value={form.primary_color} onChange={(e) => setForm((p) => ({ ...p, primary_color: e.target.value }))} className="h-9 w-14 rounded border border-slate-200" />
                <input value={form.primary_color} onChange={(e) => setForm((p) => ({ ...p, primary_color: e.target.value }))} className="flex-1 rounded-md border border-slate-200 px-3 py-2 text-sm font-mono" />
              </div>
            </label>
            <label className="block text-sm">
              <div className="mb-1 text-xs font-bold uppercase tracking-widest text-slate-500">Secondary color</div>
              <div className="flex items-center gap-2">
                <input type="color" value={form.secondary_color} onChange={(e) => setForm((p) => ({ ...p, secondary_color: e.target.value }))} className="h-9 w-14 rounded border border-slate-200" />
                <input value={form.secondary_color} onChange={(e) => setForm((p) => ({ ...p, secondary_color: e.target.value }))} className="flex-1 rounded-md border border-slate-200 px-3 py-2 text-sm font-mono" />
              </div>
            </label>
          </div>
        </div>
        <div className="flex items-center justify-between rounded-2xl border border-slate-200 bg-white p-4">
          <button onClick={() => nav(-1)} className="rounded-md border border-slate-200 px-4 py-2 text-xs font-bold text-slate-700 hover:bg-slate-50 flex items-center gap-1"><ArrowLeft className="h-3.5 w-3.5" /> Cancel</button>
          <button disabled={saving} onClick={submit} className="rounded-md bg-[#101876] px-4 py-2 text-xs font-bold text-white hover:bg-[#0c135c]" data-testid="tf-save">
            {saving ? "Saving…" : mode === "edit" ? "Save changes" : "Create template"}
          </button>
        </div>
      </div>
    </DashboardLayout>
  );
}

// =========================================================================
// CERT REGISTER (Super Admin)
// =========================================================================
export function AdminCertRegisterPage() {
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ status: "all", search: "" });
  const [page, setPage] = useState(1);
  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, page_size: 20 };
      if (filters.status !== "all") params.status = filters.status;
      if (filters.search) params.search = filters.search;
      const { data } = await api.get("/admin/certificates", { params });
      setRows(data.data.certificates);
      setTotal(data.data.total);
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setLoading(false); }
  }, [page, filters]);
  useEffect(() => { load(); }, [load]);

  return (
    <DashboardLayout title="Certificate Register" breadcrumbs={["Super Admin", "Certificates"]}>
      <div className="space-y-4" data-testid="admin-cert-register">
        <div className="flex flex-wrap items-center gap-3">
          <Link to="/app/super-admin/certificates/issue"
            className="rounded-md bg-[#101876] px-4 py-2 text-xs font-bold text-white hover:bg-[#0c135c] flex items-center gap-1" data-testid="btn-manual-issue">
            <Plus className="h-3.5 w-3.5" /> Manual issue
          </Link>
          <div className="ml-auto text-xs text-slate-500">Total: <span className="font-bold">{total}</span></div>
        </div>
        <div className="rounded-2xl border border-slate-200 bg-white p-4 grid gap-3 sm:grid-cols-4">
          <div className="relative sm:col-span-2">
            <Search className="pointer-events-none absolute left-3 top-2.5 h-4 w-4 text-slate-400" />
            <input placeholder="Search certificate number…" value={filters.search}
              onChange={(e) => { setFilters((p) => ({ ...p, search: e.target.value })); setPage(1); }}
              className="w-full rounded-md border border-slate-200 pl-9 pr-3 py-2 text-sm" data-testid="cert-search" />
          </div>
          <Select value={filters.status} onValueChange={(v) => { setFilters((p) => ({ ...p, status: v })); setPage(1); }}>
            <SelectTrigger data-testid="cert-status-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All statuses</SelectItem>
              <SelectItem value="valid">Valid</SelectItem>
              <SelectItem value="expired">Expired</SelectItem>
              <SelectItem value="revoked">Revoked</SelectItem>
              <SelectItem value="reissued">Reissued</SelectItem>
            </SelectContent>
          </Select>
        </div>
        {loading ? <LoadingBlock /> : rows.length === 0 ? (
          <EmptyState title="No certificates yet" description="Certificates will appear here as learners qualify." icon={Award} />
        ) : (
          <div className="rounded-2xl border border-slate-200 bg-white overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50/60 text-xs uppercase tracking-widest text-slate-500">
                <tr>
                  <th className="px-4 py-3 text-left">Certificate #</th>
                  <th className="px-4 py-3 text-left">Learner</th>
                  <th className="px-4 py-3 text-left">Course</th>
                  <th className="px-4 py-3 text-left">Org</th>
                  <th className="px-4 py-3 text-left">Issued</th>
                  <th className="px-4 py-3 text-left">Status</th>
                  <th className="px-4 py-3 text-right">Action</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {rows.map((c) => (
                  <tr key={c.id} className="hover:bg-slate-50/60" data-testid={`cert-row-${c.id}`}>
                    <td className="px-4 py-2 font-mono text-xs">{c.certificate_number}</td>
                    <td className="px-4 py-2">{c.learner?.full_name}<div className="text-xs text-slate-500">{c.learner?.email}</div></td>
                    <td className="px-4 py-2 text-xs">{c.course?.course_title}<div className="font-mono text-[10px] text-slate-500">{c.course?.course_code}</div></td>
                    <td className="px-4 py-2 text-xs">{c.organization?.organization_code || "—"}</td>
                    <td className="px-4 py-2 text-xs">{c.issue_date?.slice(0, 10)}</td>
                    <td className="px-4 py-2"><StatusBadge status={c.computed_status || c.certificate_status} /></td>
                    <td className="px-4 py-2 text-right">
                      <Link to={`/app/super-admin/certificates/${c.id}`} className="text-xs font-bold text-[#101876] hover:underline" data-testid={`cert-open-${c.id}`}>Open →</Link>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {total > 20 && (
          <div className="flex items-center justify-center gap-2 py-3">
            <button disabled={page === 1} onClick={() => setPage((p) => p - 1)} className="rounded-md border border-slate-200 px-3 py-1.5 text-xs disabled:opacity-40">Previous</button>
            <span className="text-xs text-slate-500">Page {page}</span>
            <button disabled={page * 20 >= total} onClick={() => setPage((p) => p + 1)} className="rounded-md border border-slate-200 px-3 py-1.5 text-xs disabled:opacity-40">Next</button>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}

// =========================================================================
// CERT DETAIL (Admin)
// =========================================================================
export function AdminCertDetailPage() {
  const { cid } = useParams();
  const nav = useNavigate();
  const [c, setC] = useState(null);
  const [reason, setReason] = useState("");
  const [showRevoke, setShowRevoke] = useState(false);
  const [showReissue, setShowReissue] = useState(false);
  const [showOverride, setShowOverride] = useState(false);
  const [overrideReason, setOverrideReason] = useState("");

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/admin/certificates/${cid}`);
      setC(data.data.certificate);
    } catch (err) { toast.error(formatApiError(err)); }
  }, [cid]);
  useEffect(() => { load(); }, [load]);

  const download = () => {
    window.open(`${BACKEND}/api/admin/certificates/${cid}/download`, "_blank");
  };
  const doRevoke = async () => {
    if (!reason) return toast.error("Reason required.");
    try {
      await api.post(`/admin/certificates/${cid}/revoke`, { reason });
      toast.success("Revoked.");
      setShowRevoke(false); setReason("");
      load();
    } catch (err) { toast.error(formatApiError(err)); }
  };
  const doReissue = async () => {
    if (!reason) return toast.error("Reason required.");
    try {
      const { data } = await api.post(`/admin/certificates/${cid}/reissue`, { reason });
      toast.success("Reissued.");
      setShowReissue(false); setReason("");
      nav(`/app/super-admin/certificates/${data.data.new_certificate_id}`);
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const doOverride = async (active) => {
    if (active && !overrideReason.trim()) return toast.error("Reason required.");
    try {
      await api.post(`/admin/certificates/${cid}/override-eligibility`, {
        override_reason: overrideReason.trim() || "Override lifted",
        active,
      });
      toast.success(active ? "Eligibility override activated." : "Override lifted.");
      setShowOverride(false); setOverrideReason("");
      load();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  if (!c) return <DashboardLayout title="Certificate"><LoadingBlock /></DashboardLayout>;
  const verifyUrl = `${window.location.origin}/verify-certificate/${c.verification_code}`;
  return (
    <DashboardLayout title="Certificate Detail" breadcrumbs={["Super Admin", "Certificates", c.certificate_number]}>
      <div className="max-w-5xl space-y-4" data-testid="cert-detail">
        <button onClick={() => nav(-1)} className="text-xs text-slate-500 hover:text-slate-700 flex items-center gap-1"><ArrowLeft className="h-3.5 w-3.5" /> Back</button>
        <div className="rounded-2xl border border-slate-200 bg-white overflow-hidden">
          <div className="bg-gradient-to-br from-[#101876] to-[#329112] p-6 text-white flex items-center justify-between">
            <div>
              <div className="text-xs opacity-80 font-bold uppercase tracking-widest">Certificate</div>
              <div className="text-xl font-bold mt-1 font-mono">{c.certificate_number}</div>
              <div className="text-xs opacity-90 mt-1">Issued to {c.learner?.full_name}</div>
            </div>
            <StatusBadge status={c.computed_status || c.certificate_status} />
          </div>
          <div className="p-6 grid gap-4 sm:grid-cols-2">
            <div>
              <div className="text-xs font-bold uppercase tracking-widest text-slate-500">Learner</div>
              <div className="text-sm mt-1">{c.learner?.full_name}</div>
              <div className="text-xs text-slate-500">{c.learner?.email}</div>
            </div>
            <div>
              <div className="text-xs font-bold uppercase tracking-widest text-slate-500">Course</div>
              <div className="text-sm mt-1">{c.course?.course_title}</div>
              <div className="text-xs font-mono text-slate-500">{c.course?.course_code}</div>
            </div>
            <div>
              <div className="text-xs font-bold uppercase tracking-widest text-slate-500">Issue date</div>
              <div className="text-sm mt-1">{c.issue_date?.slice(0, 10)}</div>
            </div>
            {c.percentage != null && (
              <div>
                <div className="text-xs font-bold uppercase tracking-widest text-slate-500">Exam score</div>
                <div className="text-sm mt-1">{c.percentage}%</div>
              </div>
            )}
            {c.organization?.organization_name && (
              <div>
                <div className="text-xs font-bold uppercase tracking-widest text-slate-500">Organization</div>
                <div className="text-sm mt-1">{c.organization?.organization_name}</div>
              </div>
            )}
            <div className="sm:col-span-2 rounded-md border border-slate-200 bg-slate-50 p-3">
              <div className="text-xs font-bold uppercase tracking-widest text-slate-500 mb-1">Public verification URL</div>
              <div className="flex items-center gap-2">
                <code className="flex-1 text-xs break-all">{verifyUrl}</code>
                <button onClick={() => { navigator.clipboard.writeText(verifyUrl); toast.success("Copied."); }}
                  className="rounded-md border border-slate-200 p-1.5 hover:bg-white" data-testid="cert-copy-verify">
                  <Copy className="h-3.5 w-3.5" />
                </button>
                <a href={verifyUrl} target="_blank" rel="noreferrer" className="rounded-md border border-slate-200 p-1.5 hover:bg-white" data-testid="cert-open-verify"><ExternalLink className="h-3.5 w-3.5" /></a>
              </div>
            </div>
            {c.revocation_reason && (
              <div className="sm:col-span-2 rounded-md border border-rose-200 bg-rose-50 p-3 text-xs text-rose-800">
                <div className="font-bold uppercase tracking-widest mb-1">Revoked</div>
                <div>{c.revocation_reason}</div>
              </div>
            )}
            {c.reissue_reason && (
              <div className="sm:col-span-2 rounded-md border border-amber-200 bg-amber-50 p-3 text-xs text-amber-800">
                <div className="font-bold uppercase tracking-widest mb-1">Reissued</div>
                <div>{c.reissue_reason}</div>
              </div>
            )}
            {c.manual_override?.active && (
              <div className="sm:col-span-2 rounded-md border border-indigo-200 bg-indigo-50 p-3 text-xs text-indigo-800" data-testid="cert-override-banner">
                <div className="font-bold uppercase tracking-widest mb-1">Eligibility override active</div>
                <div>Reason: {c.manual_override.override_reason}</div>
                <div className="text-[10px] mt-0.5 text-indigo-600">By {c.manual_override.override_by?.slice(0, 8)} at {c.manual_override.override_at?.slice(0, 16)}</div>
              </div>
            )}
          </div>
          <div className="border-t border-slate-100 p-4 flex flex-wrap gap-2 justify-end">
            <button onClick={download} className="rounded-md border border-slate-300 px-4 py-2 text-xs font-bold text-slate-700 hover:bg-slate-50 flex items-center gap-1" data-testid="btn-cert-download">
              <Download className="h-3.5 w-3.5" /> Download PDF
            </button>
            {c.certificate_status !== "revoked" && c.certificate_status !== "reissued" && (
              <>
                <button onClick={() => setShowOverride(true)} className="rounded-md border border-indigo-300 bg-white px-4 py-2 text-xs font-bold text-indigo-700 hover:bg-indigo-50" data-testid="btn-cert-override">
                  {c.manual_override?.active ? "Override active — manage" : "Override eligibility"}
                </button>
                <button onClick={() => setShowReissue(true)} className="rounded-md bg-amber-600 px-4 py-2 text-xs font-bold text-white hover:bg-amber-700" data-testid="btn-cert-reissue">Reissue</button>
                <button onClick={() => setShowRevoke(true)} className="rounded-md bg-rose-600 px-4 py-2 text-xs font-bold text-white hover:bg-rose-700" data-testid="btn-cert-revoke">Revoke</button>
              </>
            )}
          </div>
        </div>
      </div>
      <Dialog open={showRevoke} onOpenChange={setShowRevoke}>
        <DialogContent>
          <DialogHeader><DialogTitle>Revoke certificate</DialogTitle></DialogHeader>
          <textarea rows={3} value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Reason for revocation…"
            className="w-full rounded-md border border-slate-200 px-3 py-2 text-sm" data-testid="revoke-reason" />
          <DialogFooter>
            <button onClick={() => setShowRevoke(false)} className="rounded-md border border-slate-200 px-4 py-2 text-xs font-bold hover:bg-slate-50">Cancel</button>
            <button onClick={doRevoke} className="rounded-md bg-rose-600 px-4 py-2 text-xs font-bold text-white hover:bg-rose-700" data-testid="revoke-confirm">Revoke</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <Dialog open={showReissue} onOpenChange={setShowReissue}>
        <DialogContent>
          <DialogHeader><DialogTitle>Reissue certificate</DialogTitle></DialogHeader>
          <textarea rows={3} value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Reason for reissue…"
            className="w-full rounded-md border border-slate-200 px-3 py-2 text-sm" data-testid="reissue-reason" />
          <DialogFooter>
            <button onClick={() => setShowReissue(false)} className="rounded-md border border-slate-200 px-4 py-2 text-xs font-bold hover:bg-slate-50">Cancel</button>
            <button onClick={doReissue} className="rounded-md bg-amber-600 px-4 py-2 text-xs font-bold text-white hover:bg-amber-700" data-testid="reissue-confirm">Reissue</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <Dialog open={showOverride} onOpenChange={setShowOverride}>
        <DialogContent>
          <DialogHeader><DialogTitle>Override eligibility gate</DialogTitle></DialogHeader>
          <div className="text-xs text-slate-600 mb-2">
            When activated, this certificate can be downloaded even if the required post-test is not attempted or not passed. Recorded in the audit log.
          </div>
          <textarea rows={3} value={overrideReason} onChange={(e) => setOverrideReason(e.target.value)} placeholder="Reason for override…"
            className="w-full rounded-md border border-slate-200 px-3 py-2 text-sm" data-testid="override-reason" />
          <DialogFooter>
            <button onClick={() => setShowOverride(false)} className="rounded-md border border-slate-200 px-4 py-2 text-xs font-bold hover:bg-slate-50">Cancel</button>
            {c.manual_override?.active && (
              <button onClick={() => doOverride(false)} className="rounded-md border border-slate-300 bg-white px-4 py-2 text-xs font-bold text-slate-700 hover:bg-slate-50" data-testid="override-lift">
                Lift override
              </button>
            )}
            <button onClick={() => doOverride(true)} className="rounded-md bg-indigo-600 px-4 py-2 text-xs font-bold text-white hover:bg-indigo-700" data-testid="override-activate">
              {c.manual_override?.active ? "Update reason" : "Activate override"}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
}

// =========================================================================
// MANUAL ISSUE PAGE
// =========================================================================
export function AdminManualIssuePage() {
  const nav = useNavigate();
  const [templates, setTemplates] = useState([]);
  const [courses, setCourses] = useState([]);
  const [users, setUsers] = useState([]);
  const [form, setForm] = useState({
    template_id: "", course_id: "", learner_user_id: "", remarks: "",
    director_name: "", director_designation: "", director_signature_path: "",
    trainer_name: "", trainer_designation: "", trainer_signature_path: "",
    course_start_date: "", course_end_date: "",
  });
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState({ director: false, trainer: false });
  // Local blob-URL previews so admin sees the uploaded signature
  // immediately (no round-trip through /api/files/*).
  const [sigPreview, setSigPreview] = useState({ director: "", trainer: "" });

  useEffect(() => {
    (async () => {
      try { const { data } = await api.get("/admin/certificate-templates"); setTemplates(data.data.templates.filter((t) => t.status === "active")); } catch { /* */ }
      try { const { data } = await api.get("/admin/courses", { params: { page_size: 500, status: "published" } }); setCourses(data.data.courses); } catch { /* */ }
      try { const { data } = await api.get("/admin/staff", { params: { page_size: 500 } }); setUsers(data.data.users || []); } catch { /* */ }
    })();
  }, []);

  const uploadSig = async (which, file) => {
    if (!file) return;
    setUploading((u) => ({ ...u, [which]: true }));
    // Show a local preview immediately even while the upload is in-flight
    const localUrl = URL.createObjectURL(file);
    setSigPreview((prev) => {
      if (prev[which]) URL.revokeObjectURL(prev[which]);
      return { ...prev, [which]: localUrl };
    });
    try {
      const fd = new FormData(); fd.append("file", file);
      const { data } = await api.post("/admin/certificates/upload-signature", fd,
        { headers: { "Content-Type": "multipart/form-data" } });
      setForm((p) => ({ ...p, [`${which}_signature_path`]: data.data.path }));
      toast.success(`${which === "director" ? "Director" : "Trainer"} signature uploaded.`);
    } catch (err) {
      // Roll back the preview if the upload actually failed so the
      // admin doesn't think the file is on the server.
      setSigPreview((prev) => {
        if (prev[which]) URL.revokeObjectURL(prev[which]);
        return { ...prev, [which]: "" };
      });
      toast.error(formatApiError(err));
    }
    finally { setUploading((u) => ({ ...u, [which]: false })); }
  };

  const clearSig = (which) => {
    setSigPreview((prev) => {
      if (prev[which]) URL.revokeObjectURL(prev[which]);
      return { ...prev, [which]: "" };
    });
    setForm((p) => ({ ...p, [`${which}_signature_path`]: "" }));
  };

  const submit = async () => {
    if (!form.template_id || !form.course_id || !form.learner_user_id) return toast.error("All fields required.");
    setSaving(true);
    try {
      const { data } = await api.post("/admin/certificates/manual-issue", form);
      toast.success("Certificate issued.");
      nav(`/app/super-admin/certificates/${data.data.certificate.id}`);
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setSaving(false); }
  };

  return (
    <DashboardLayout title="Manual Certificate Issue" breadcrumbs={["Super Admin", "Certificates", "Issue"]}>
      <div className="max-w-2xl space-y-4" data-testid="manual-issue">
        <div className="rounded-2xl border border-slate-200 bg-white p-6 space-y-4">
          <label className="block text-sm">
            <div className="mb-1 text-xs font-bold uppercase tracking-widest text-slate-500">Learner *</div>
            <select value={form.learner_user_id} onChange={(e) => setForm((p) => ({ ...p, learner_user_id: e.target.value }))}
              className="w-full rounded-md border border-slate-200 px-3 py-2 text-sm" data-testid="mi-learner">
              <option value="">Select learner</option>
              {users.map((u) => <option key={u.id} value={u.id}>{u.full_name} — {u.email}</option>)}
            </select>
          </label>
          <label className="block text-sm">
            <div className="mb-1 text-xs font-bold uppercase tracking-widest text-slate-500">Course *</div>
            <select value={form.course_id} onChange={(e) => setForm((p) => ({ ...p, course_id: e.target.value }))}
              className="w-full rounded-md border border-slate-200 px-3 py-2 text-sm" data-testid="mi-course">
              <option value="">Select course</option>
              {courses.map((c) => <option key={c.id} value={c.id}>{c.course_code} — {c.course_title}</option>)}
            </select>
          </label>
          <label className="block text-sm">
            <div className="mb-1 text-xs font-bold uppercase tracking-widest text-slate-500">Template *</div>
            <select value={form.template_id} onChange={(e) => setForm((p) => ({ ...p, template_id: e.target.value }))}
              className="w-full rounded-md border border-slate-200 px-3 py-2 text-sm" data-testid="mi-template">
              <option value="">Select template</option>
              {templates.map((t) => <option key={t.id} value={t.id}>{t.template_code} — {t.template_name}</option>)}
            </select>
          </label>
          <label className="block text-sm">
            <div className="mb-1 text-xs font-bold uppercase tracking-widest text-slate-500">Remarks</div>
            <textarea rows={2} value={form.remarks} onChange={(e) => setForm((p) => ({ ...p, remarks: e.target.value }))}
              className="w-full rounded-md border border-slate-200 px-3 py-2 text-sm" />
          </label>

          {/* Course drive window (optional) — printed on the certificate */}
          <div className="rounded-md border border-slate-200 bg-slate-50 p-3 space-y-2" data-testid="mi-course-window">
            <div className="text-[11px] font-bold uppercase tracking-widest text-[#101876]">Course drive window (optional)</div>
            <div className="text-[11px] text-slate-500">
              Dono dates fill karo — certificate pe ye line print hogi: <b>Course Duration: 01 Jan 2026 – 31 Mar 2026 (3 months)</b>.
            </div>
            <div className="grid gap-2 sm:grid-cols-2">
              <label className="block text-sm">
                <div className="mb-1 text-xs font-bold uppercase tracking-widest text-slate-500">Course start date</div>
                <input type="date" value={form.course_start_date}
                  max={form.course_end_date || undefined}
                  onChange={(e) => setForm((p) => ({ ...p, course_start_date: e.target.value }))}
                  className="w-full rounded-md border border-slate-200 px-3 py-2 text-sm"
                  data-testid="mi-course-start-date" />
              </label>
              <label className="block text-sm">
                <div className="mb-1 text-xs font-bold uppercase tracking-widest text-slate-500">Course end date</div>
                <input type="date" value={form.course_end_date}
                  min={form.course_start_date || undefined}
                  onChange={(e) => setForm((p) => ({ ...p, course_end_date: e.target.value }))}
                  className="w-full rounded-md border border-slate-200 px-3 py-2 text-sm"
                  data-testid="mi-course-end-date" />
              </label>
            </div>
            {form.course_start_date && form.course_end_date && (() => {
              const s = new Date(form.course_start_date);
              const e = new Date(form.course_end_date);
              if (e < s) return <div className="text-[11px] font-bold text-rose-700">End date start date se pehle nahi ho sakti.</div>;
              const dayDiff = Math.round((e - s) / 86400000);
              const m = (e.getFullYear() - s.getFullYear()) * 12 + (e.getMonth() - s.getMonth()) + 1;
              let label;
              if (dayDiff < 30) label = dayDiff === 1 ? "1 day" : `${dayDiff} days`;
              else if (m < 12) label = m === 1 ? "1 month" : `${m} months`;
              else { const y = Math.floor(m / 12); const r = m % 12; label = `${y} year${y > 1 ? "s" : ""}${r ? ` ${r} month${r > 1 ? "s" : ""}` : ""}`; }
              return <div className="text-[11px] font-bold text-emerald-700" data-testid="mi-duration-preview">Preview: {label}</div>;
            })()}
          </div>
        </div>

        {/* Signature overrides — Director (MD) + Course Trainer */}
        <div className="rounded-2xl border-2 border-[#329112]/30 bg-[#329112]/5 p-6 space-y-4">
          <div className="text-xs font-bold uppercase tracking-widest text-[#256b0d]">
            Signatures on the certificate
          </div>
          <p className="text-[11px] text-slate-600 -mt-2">
            Leave any field blank to fall back to the certificate template's Director / the Course's saved Trainer signature.
            Upload a transparent PNG for best print quality (max 5 MB).
          </p>

          {/* Director / MD */}
          <div className="rounded-md border border-slate-200 bg-white p-3 space-y-2">
            <div className="text-[11px] font-bold uppercase tracking-widest text-[#101876]">Left signature — Director / Managing Director</div>
            <div className="grid gap-2 sm:grid-cols-2">
              <input placeholder="Director name (e.g. Dinesh Sharma)"
                value={form.director_name}
                onChange={(e) => setForm((p) => ({ ...p, director_name: e.target.value }))}
                className="rounded-md border border-slate-200 px-3 py-2 text-sm"
                data-testid="mi-director-name" />
              <input placeholder="Designation (e.g. Managing Director)"
                value={form.director_designation}
                onChange={(e) => setForm((p) => ({ ...p, director_designation: e.target.value }))}
                className="rounded-md border border-slate-200 px-3 py-2 text-sm"
                data-testid="mi-director-designation" />
            </div>
            <div className="flex items-center gap-3">
              <input type="file" accept="image/*" data-testid="mi-director-signature"
                onChange={(e) => uploadSig("director", e.target.files?.[0])}
                className="text-xs" />
              {uploading.director && <Loader2 className="h-4 w-4 animate-spin text-slate-500" />}
              {form.director_signature_path && (
                <span className="text-[11px] font-bold text-emerald-700">✓ Uploaded</span>
              )}
            </div>
            {sigPreview.director && (
              <div className="mt-2 flex items-center gap-3 rounded-md border border-slate-200 bg-white p-2" data-testid="mi-director-signature-preview">
                <img src={sigPreview.director} alt="Director signature preview"
                  className="h-14 max-w-[220px] object-contain bg-[repeating-linear-gradient(45deg,#f8fafc,#f8fafc_6px,#f1f5f9_6px,#f1f5f9_12px)] rounded" />
                <button type="button" onClick={() => clearSig("director")}
                  className="text-[11px] font-bold text-rose-600 hover:underline"
                  data-testid="mi-director-signature-remove">Remove</button>
              </div>
            )}
          </div>

          {/* Trainer */}
          <div className="rounded-md border border-slate-200 bg-white p-3 space-y-2">
            <div className="text-[11px] font-bold uppercase tracking-widest text-[#101876]">Right signature — Course Trainer</div>
            <div className="grid gap-2 sm:grid-cols-2">
              <input placeholder="Trainer name (e.g. Ms Rupali Chopra)"
                value={form.trainer_name}
                onChange={(e) => setForm((p) => ({ ...p, trainer_name: e.target.value }))}
                className="rounded-md border border-slate-200 px-3 py-2 text-sm"
                data-testid="mi-trainer-name" />
              <input placeholder="Designation (e.g. Course Trainer)"
                value={form.trainer_designation}
                onChange={(e) => setForm((p) => ({ ...p, trainer_designation: e.target.value }))}
                className="rounded-md border border-slate-200 px-3 py-2 text-sm"
                data-testid="mi-trainer-designation" />
            </div>
            <div className="flex items-center gap-3">
              <input type="file" accept="image/*" data-testid="mi-trainer-signature"
                onChange={(e) => uploadSig("trainer", e.target.files?.[0])}
                className="text-xs" />
              {uploading.trainer && <Loader2 className="h-4 w-4 animate-spin text-slate-500" />}
              {form.trainer_signature_path && (
                <span className="text-[11px] font-bold text-emerald-700">✓ Uploaded</span>
              )}
            </div>
            {sigPreview.trainer && (
              <div className="mt-2 flex items-center gap-3 rounded-md border border-slate-200 bg-white p-2" data-testid="mi-trainer-signature-preview">
                <img src={sigPreview.trainer} alt="Trainer signature preview"
                  className="h-14 max-w-[220px] object-contain bg-[repeating-linear-gradient(45deg,#f8fafc,#f8fafc_6px,#f1f5f9_6px,#f1f5f9_12px)] rounded" />
                <button type="button" onClick={() => clearSig("trainer")}
                  className="text-[11px] font-bold text-rose-600 hover:underline"
                  data-testid="mi-trainer-signature-remove">Remove</button>
              </div>
            )}
          </div>
        </div>

        <div className="flex justify-end gap-2">
          <button onClick={() => nav(-1)} className="rounded-md border border-slate-200 px-4 py-2 text-xs font-bold text-slate-700 hover:bg-slate-50">Cancel</button>
          <button disabled={saving} onClick={submit} className="rounded-md bg-[#101876] px-4 py-2 text-xs font-bold text-white hover:bg-[#0c135c]" data-testid="mi-submit">
            {saving ? "Issuing…" : "Issue certificate"}
          </button>
        </div>
      </div>
    </DashboardLayout>
  );
}

// =========================================================================
// COURSE CERTIFICATE RULE (attached to course settings)
// =========================================================================
export function CertRulePage() {
  const { courseId } = useParams();
  const nav = useNavigate();
  const [rule, setRule] = useState(null);
  const [course, setCourse] = useState(null);
  const [templates, setTemplates] = useState([]);
  const [exams, setExams] = useState([]);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/admin/courses/${courseId}`);
      setCourse(data.data.course);
    } catch { /* */ }
    try {
      const { data } = await api.get(`/admin/courses/${courseId}/certificate-rule`);
      setRule(data.data.rule || {
        certificate_applicable: true, certificate_template_id: "",
        certificate_trigger: "course_and_exam",
        minimum_course_progress_required: 100, required_exam_id: null,
        minimum_passing_percentage: 60,
        validity_type: "fixed_years", validity_days: 0, validity_months: 0, validity_years: 1,
        renewal_required: true, auto_generate_certificate: true, require_admin_approval: false,
        status: "active",
      });
    } catch (err) { toast.error(formatApiError(err)); }
    try {
      const { data } = await api.get("/admin/certificate-templates");
      setTemplates(data.data.templates);
    } catch { /* */ }
    try {
      const { data } = await api.get("/admin/exams", { params: { course_id: courseId, status: "published" } });
      setExams(data.data.exams);
    } catch { /* */ }
  }, [courseId]);
  useEffect(() => { load(); }, [load]);

  const save = async () => {
    setSaving(true);
    try {
      await api.post(`/admin/courses/${courseId}/certificate-rule`, rule);
      toast.success("Certificate rule saved.");
      load();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setSaving(false); }
  };

  if (!rule) return <DashboardLayout title="Certificate rule"><LoadingBlock /></DashboardLayout>;
  const S = (patch) => setRule((p) => ({ ...p, ...patch }));
  return (
    <DashboardLayout title="Certificate Rule" breadcrumbs={["Super Admin", "Courses", course?.course_code || courseId, "Certificate rule"]}>
      <div className="max-w-4xl space-y-4" data-testid="cert-rule-form">
        <button onClick={() => nav(-1)} className="text-xs text-slate-500 hover:text-slate-700 flex items-center gap-1"><ArrowLeft className="h-3.5 w-3.5" /> Back</button>
        <div className="rounded-2xl border border-slate-200 bg-white p-6 space-y-4">
          <div className="text-xs font-bold uppercase tracking-widest text-slate-500">Basic</div>
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" checked={rule.certificate_applicable} onChange={(e) => S({ certificate_applicable: e.target.checked })} data-testid="cr-applicable" />
            <span>This course issues a certificate</span>
          </label>
          <div className="grid gap-3 sm:grid-cols-2">
            <label className="block text-sm">
              <div className="mb-1 text-xs font-bold uppercase tracking-widest text-slate-500">Template *</div>
              <select value={rule.certificate_template_id || ""} onChange={(e) => S({ certificate_template_id: e.target.value })}
                className="w-full rounded-md border border-slate-200 px-3 py-2 text-sm" data-testid="cr-template">
                <option value="">Select template</option>
                {templates.map((t) => <option key={t.id} value={t.id}>{t.template_code} — {t.template_name}</option>)}
              </select>
            </label>
            <label className="block text-sm">
              <div className="mb-1 text-xs font-bold uppercase tracking-widest text-slate-500">Trigger</div>
              <select value={rule.certificate_trigger} onChange={(e) => S({ certificate_trigger: e.target.value })}
                className="w-full rounded-md border border-slate-200 px-3 py-2 text-sm">
                <option value="course_completion">Course Completion Only</option>
                <option value="exam_pass">Exam Pass Only</option>
                <option value="course_and_exam">Course Completion + Exam Pass</option>
                <option value="manual_approval">Manual Approval Required</option>
              </select>
            </label>
            <label className="block text-sm">
              <div className="mb-1 text-xs font-bold uppercase tracking-widest text-slate-500">Min course progress %</div>
              <input type="number" value={rule.minimum_course_progress_required} onChange={(e) => S({ minimum_course_progress_required: Number(e.target.value) })} className="w-full rounded-md border border-slate-200 px-3 py-2 text-sm" />
            </label>
            <label className="block text-sm">
              <div className="mb-1 text-xs font-bold uppercase tracking-widest text-slate-500">Required exam</div>
              <select value={rule.required_exam_id || ""} onChange={(e) => S({ required_exam_id: e.target.value })}
                className="w-full rounded-md border border-slate-200 px-3 py-2 text-sm">
                <option value="">— None —</option>
                {exams.map((e) => <option key={e.id} value={e.id}>{e.exam_code}</option>)}
              </select>
            </label>
            <label className="block text-sm">
              <div className="mb-1 text-xs font-bold uppercase tracking-widest text-slate-500">Min passing %</div>
              <input type="number" value={rule.minimum_passing_percentage} onChange={(e) => S({ minimum_passing_percentage: Number(e.target.value) })} className="w-full rounded-md border border-slate-200 px-3 py-2 text-sm" />
            </label>
          </div>
        </div>
        <div className="rounded-2xl border border-slate-200 bg-white p-6 space-y-4">
          <div className="text-xs font-bold uppercase tracking-widest text-slate-500">Validity & renewal</div>
          <div className="grid gap-3 sm:grid-cols-4">
            <label className="block text-sm">
              <div className="mb-1 text-xs font-bold uppercase tracking-widest text-slate-500">Validity type</div>
              <select value={rule.validity_type} onChange={(e) => S({ validity_type: e.target.value })}
                className="w-full rounded-md border border-slate-200 px-3 py-2 text-sm">
                <option value="no_expiry">No expiry</option>
                <option value="fixed_days">Fixed days</option>
                <option value="fixed_months">Fixed months</option>
                <option value="fixed_years">Fixed years</option>
                <option value="specific_date">Specific date</option>
              </select>
            </label>
            <label className="block text-sm">
              <div className="mb-1 text-xs font-bold uppercase tracking-widest text-slate-500">Years</div>
              <input type="number" value={rule.validity_years} onChange={(e) => S({ validity_years: Number(e.target.value) })} className="w-full rounded-md border border-slate-200 px-3 py-2 text-sm" />
            </label>
            <label className="block text-sm">
              <div className="mb-1 text-xs font-bold uppercase tracking-widest text-slate-500">Months</div>
              <input type="number" value={rule.validity_months} onChange={(e) => S({ validity_months: Number(e.target.value) })} className="w-full rounded-md border border-slate-200 px-3 py-2 text-sm" />
            </label>
            <label className="block text-sm">
              <div className="mb-1 text-xs font-bold uppercase tracking-widest text-slate-500">Days</div>
              <input type="number" value={rule.validity_days} onChange={(e) => S({ validity_days: Number(e.target.value) })} className="w-full rounded-md border border-slate-200 px-3 py-2 text-sm" />
            </label>
          </div>
          <div className="grid gap-2 sm:grid-cols-3">
            <label className="flex items-center gap-2 text-sm rounded-md border border-slate-200 p-3">
              <input type="checkbox" checked={rule.renewal_required} onChange={(e) => S({ renewal_required: e.target.checked })} />
              <span>Renewal required</span>
            </label>
            <label className="flex items-center gap-2 text-sm rounded-md border border-slate-200 p-3">
              <input type="checkbox" checked={rule.auto_generate_certificate} onChange={(e) => S({ auto_generate_certificate: e.target.checked })} />
              <span>Auto generate</span>
            </label>
            <label className="flex items-center gap-2 text-sm rounded-md border border-slate-200 p-3">
              <input type="checkbox" checked={rule.require_admin_approval} onChange={(e) => S({ require_admin_approval: e.target.checked })} />
              <span>Require admin approval</span>
            </label>
          </div>
        </div>
        <div className="flex justify-end">
          <button disabled={saving} onClick={save} className="rounded-md bg-[#101876] px-4 py-2 text-xs font-bold text-white hover:bg-[#0c135c]" data-testid="cr-save">
            {saving ? "Saving…" : "Save rule"}
          </button>
        </div>
      </div>
    </DashboardLayout>
  );
}

// =========================================================================
// CERT RENEWAL PAGE
// =========================================================================
export function AdminCertRenewalPage() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/certificate-renewals");
      setRows(data.data.renewals);
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);
  const runCheck = async () => {
    try {
      await api.post("/admin/certificate-renewals/check-due");
      toast.success("Renewal check complete.");
      load();
    } catch (err) { toast.error(formatApiError(err)); }
  };
  return (
    <DashboardLayout title="Certificate Renewals" breadcrumbs={["Super Admin", "Certificates", "Renewals"]}>
      <div className="space-y-4" data-testid="admin-renewals">
        <div className="flex items-center gap-3">
          <button onClick={runCheck} className="rounded-md bg-[#101876] px-4 py-2 text-xs font-bold text-white hover:bg-[#0c135c] flex items-center gap-1" data-testid="btn-renewal-check">
            <RefreshCw className="h-3.5 w-3.5" /> Run renewal check
          </button>
        </div>
        {loading ? <LoadingBlock /> : rows.length === 0 ? (
          <EmptyState title="No renewals" description="Certificate renewals will appear as certificates approach their expiry date." icon={RefreshCw} />
        ) : (
          <div className="rounded-2xl border border-slate-200 bg-white overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50/60 text-xs uppercase tracking-widest text-slate-500">
                <tr><th className="px-4 py-3 text-left">Learner</th><th className="px-4 py-3 text-left">Course</th><th className="px-4 py-3 text-left">Due date</th><th className="px-4 py-3 text-left">Status</th></tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {rows.map((r) => (
                  <tr key={r.id}>
                    <td className="px-4 py-2">{r.learner?.full_name}<div className="text-xs text-slate-500">{r.learner?.email}</div></td>
                    <td className="px-4 py-2 text-xs">{r.course?.course_title}</td>
                    <td className="px-4 py-2 text-xs">{r.renewal_due_date?.slice(0, 10)}</td>
                    <td className="px-4 py-2"><StatusBadge status={r.renewal_status} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}
